import { useEffect, useMemo, useRef, useState } from "react";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import { GoogleTasksSourceSettingsApi } from "./GoogleTasksSourceSettings.Api";
import type { GoogleTasksSourceSettingsViewModel } from "./GoogleTasksSourceSettings.ViewModel.types";
import type {
  GoogleTaskList,
  GoogleTasksConfiguration,
} from "./GoogleTasksSourceSettings.types";

const NONE_OPTION = "__none";

export const useGoogleTasksSourceSettingsViewModel = (
  model: SourceSettingsModel,
): GoogleTasksSourceSettingsViewModel => {
  const editingConnectionId = model.editingConnection.id;
  const onUpdateRef = useRef(model.onUpdate);
  const sourceSettingsApiRef = useRef(model.sourceSettingsApi);
  const editingConnectionRef = useRef(model.editingConnection);
  const initialisedConnectionIdRef = useRef<string | null>(null);
  const previousProfileIdentityRef = useRef(
    `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`,
  );

  const [lists, setLists] = useState<GoogleTaskList[]>([]);
  const [listsLoaded, setListsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    onUpdateRef.current = model.onUpdate;
  }, [model.onUpdate]);

  useEffect(() => {
    sourceSettingsApiRef.current = model.sourceSettingsApi;
  }, [model.sourceSettingsApi]);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
  }, [model.editingConnection]);

  const isVisible =
    model.editingConnection.sourceTypeId.trim().toLowerCase() ===
      SourceTypeIds.GoogleTasks && model.editingConnection.isAuthorised;

  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<GoogleTasksConfiguration>(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );

  const configurationRef = useRef(configuration);
  useEffect(() => {
    configurationRef.current = configuration;
  }, [configuration]);

  // Initialise defaults on first render for this connection
  useEffect(() => {
    if (!isVisible) {
      initialisedConnectionIdRef.current = null;
      return;
    }

    if (initialisedConnectionIdRef.current === editingConnectionId) {
      return;
    }

    initialisedConnectionIdRef.current = editingConnectionId;

    updateConnectionConfiguration<GoogleTasksConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        taskLists: cfg.taskLists ?? [],
      }),
      { isDefault: true },
    );
  }, [editingConnectionId, isVisible]);

  // Reset on profile/auth change
  useEffect(() => {
    const currentProfileIdentity = `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`;
    const previousProfileIdentity = previousProfileIdentityRef.current;
    previousProfileIdentityRef.current = currentProfileIdentity;

    if (!isVisible || previousProfileIdentity === currentProfileIdentity) {
      return;
    }

    setLists([]);

    updateConnectionConfiguration<GoogleTasksConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        taskLists: [],
        listForTaskCreation: undefined,
      }),
    );
  }, [
    isVisible,
    model.editingConnection.isAuthorised,
    model.editingConnection.profileId,
    model.editingConnection.profileName,
  ]);

  // Load lists
  useEffect(() => {
    const loadLists = async (): Promise<void> => {
      if (!isVisible || !model.boardId || !editingConnectionId) {
        setLists([]);
        setListsLoaded(true);
        return;
      }

      const googleTasksApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<GoogleTasksSourceSettingsApi>(
          SourceTypeIds.GoogleTasks,
        );
      if (!googleTasksApi) {
        setLists([]);
        setListsLoaded(true);
        return;
      }

      setListsLoaded(false);
      setHasError(false);
      try {
        const nextLists = await googleTasksApi.getLists(
          model.boardId,
          editingConnectionId,
        );
        setLists(nextLists);

        const currentConfig = configurationRef.current;
        if (
          (!currentConfig.taskLists || currentConfig.taskLists.length === 0) &&
          nextLists.length > 0
        ) {
          // Every list is selected by default; saving them all is what makes
          // the connection load what this shows.
          updateConnectionConfiguration<GoogleTasksConfiguration>(
            editingConnectionRef.current,
            onUpdateRef.current,
            (cfg) =>
              cfg.taskLists && cfg.taskLists.length > 0
                ? cfg
                : { ...cfg, taskLists: nextLists },
            { isDefault: true },
          );
        }
      } catch {
        setLists([]);
        setHasError(true);
      } finally {
        setListsLoaded(true);
      }
    };

    void loadLists();
  }, [editingConnectionId, isVisible, model.boardId]);

  return {
    isVisible,
    lists,
    listsLoaded,
    hasError,
    selectedListIds: (configuration.taskLists ?? []).map((l) => l.id),
    noneOptionValue: NONE_OPTION,

    onSelectLists: (ids: string[]) => {
      const selectedLists = lists.filter((l) => ids.includes(l.id));
      updateConnectionConfiguration<GoogleTasksConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({ ...cfg, taskLists: selectedLists }),
      );
    },
  };
};
